import { isEmpty, isNil } from 'ramda';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import externalApplicationLinksConfig from '@/Config/ExternalApplicationLinksConfig';
import { BrandType } from '@skytvnz/sky-app-store/lib/types/enums/BrandType';
import {
  Episode,
  LinearChannel,
  Maybe,
  Movie,
  Show,
  SkySubscription,
} from '@skytvnz/sky-app-store/lib/types/graph-ql';

import {
  recordAccontLinkPursued,
  recordLinkAccountOrUpgradeEncountered,
  recordUpgradeEncountered,
  recordUpgradeMessageDismissed,
  recordUpgradePursued,
} from '@/Analytics/SubscriptionUpgrade';
import { ButtonStyle } from '@/Components/Modal/SimpleModal';
import useSimpleModal from '@/Components/Modal/useSimpleModal';
import useAuth from '@/Hooks/useAuth';
import useLinkAccount, { AccountType } from '@/Hooks/useLinkAccount';
import { selectors, utils } from '@/Store';
import { CustomerPackage, ViewingMethods } from '@/lib/graph-ql';
import { VIEWING_METHODS } from './PUJ/constants';

export const LINK_OR_SUBSCRIBE_TITLE = 'Please Link or Subscribe';
export const getLinkOrSubscribeSubTitle = (showRecordMessage?: boolean | undefined) => (
  <p>
    To {showRecordMessage ? 'record' : 'watch'} this content, please link your Sky account or
    subscribe on <strong>sky.co.nz</strong>
  </p>
);
export const LINK_ACCOUNT_TEXT = 'LINK Sky ACCOUNT';
export const SUBSCRIBE_SKY_TEXT = 'SUBSCRIBE TO Sky';
export const SUBSCRIPTIONS_MODAL_TITLE = 'Upgrade your Sky Subscription';
export const UPGRADE_TEXT = 'Upgrade To Watch';
export const LOGIN_OR_REGISTER_TEXT = 'LOGIN OR REGISTER';

export const createSubscriptionsModalContent = (subscriptions: Maybe<SkySubscription>[]) => {
  const titles = subscriptions
    .filter(sub => sub?.offerId)
    .reduce<string[]>((acc, subscription) => {
      if (subscription) {
        acc.push(subscription.title);
      }
      return acc;
    }, []);

  return `Sorry this content isn’t included in your current Sky subscription package. \n
  Please upgrade  ${titles.length ? `to ${titles.join(' or ')} package` : ``} to watch.`;
};

interface ModalParameters {
  categoryName: string | undefined;
  contentId: string;
  contentTitle: string | undefined;
  contentType: string | undefined;
  viewName: string | undefined;
  channelName?: string[];
}

export const getTitleFromSubscription = (subscriptions: Maybe<SkySubscription>[]): string[] => {
  return subscriptions.reduce<string[]>((acc, subscription) => {
    if (subscription) {
      acc.push(subscription.title);
    }
    return acc;
  }, []);
};

export const useLinkOrSubscribeModal = () => {
  const { openModal } = useSimpleModal();
  const userSubscriptions = useSelector(selectors.subscription.subscriptions);

  const showLinkOrSubscribeModal = useCallback(
    (
      subscriptions?: Maybe<SkySubscription>[],
      params?: ModalParameters,
      showRecordMessage?: boolean,
    ) => {
      const subscriptionTitles = getTitleFromSubscription(userSubscriptions || []);
      const requiredSubscriptionTitles = getTitleFromSubscription(
        (subscriptions as SkySubscription[]) || [],
      );

      if (params) {
        recordLinkAccountOrUpgradeEncountered(
          params.categoryName,
          params.contentId,
          params.contentTitle,
          params.contentType,
          params.viewName,
          subscriptionTitles,
          requiredSubscriptionTitles,
          params.channelName,
        );
      }

      openModal({
        title: LINK_OR_SUBSCRIBE_TITLE,
        content: getLinkOrSubscribeSubTitle(showRecordMessage),
        okText: LINK_ACCOUNT_TEXT,
        cancelable: true,
        cancelText: SUBSCRIBE_SKY_TEXT,
        cancelButtonStyle: ButtonStyle.PRIMARY,
        onClose: () => {},
        onCancel: () => {
          recordUpgradePursued(subscriptionTitles, requiredSubscriptionTitles);
          window.open(externalApplicationLinksConfig.subscribeUrl);
        },
        onOk: () => {
          recordAccontLinkPursued();
          window.open(externalApplicationLinksConfig.linkAccountUrl);
        },
      });
    },
    [openModal, userSubscriptions],
  );
  return showLinkOrSubscribeModal;
};

export const useSubscriptionModal = () => {
  const { openModal } = useSimpleModal();
  const showLinkOrSubscribeModal = useLinkOrSubscribeModal();
  const { isAccountLinked } = useLinkAccount();
  const userSubscriptions = useSelector(selectors.subscription.subscriptions);

  const showUpgradeSubscriptionModal = useCallback(
    (
      subscriptions?: Maybe<SkySubscription>[],
      params?: ModalParameters,
      showRecordMessage?: boolean,
      customOnOk?: () => Promise<void>,
    ) => {
      if (subscriptions) {
        const subscriptionTitles = getTitleFromSubscription(userSubscriptions);
        const requiredSubscriptionTitles = getTitleFromSubscription(subscriptions);
        if (!isAccountLinked(AccountType.SkyDth)) {
          showLinkOrSubscribeModal(subscriptions, params, showRecordMessage);
          return;
        }

        if (params) {
          recordUpgradeEncountered(
            params.categoryName,
            params.contentId,
            params.contentTitle,
            params.contentType,
            params.viewName,
            subscriptionTitles,
            requiredSubscriptionTitles,
            params.channelName,
          );
        }

        openModal({
          title: SUBSCRIPTIONS_MODAL_TITLE,
          content: createSubscriptionsModalContent(subscriptions),
          okText: UPGRADE_TEXT,
          cancelable: true,
          // TODO: Upgrade to watch action
          onOk: async () => {
            recordUpgradePursued(subscriptionTitles, requiredSubscriptionTitles);
            if (customOnOk) {
              await customOnOk();
            } else {
              window.open(externalApplicationLinksConfig.upgradeSubscriptionUrl);
            }
          },
          onCancel: () => {
            recordUpgradeMessageDismissed(subscriptionTitles, requiredSubscriptionTitles);
          },
          onClose: () => {
            recordUpgradeMessageDismissed(subscriptionTitles, requiredSubscriptionTitles);
          },
        });
      }
    },
    [openModal, isAccountLinked, showLinkOrSubscribeModal, userSubscriptions],
  );

  return showUpgradeSubscriptionModal;
};

export const useVodRequiredSubscriptions = () => {
  const auth = useAuth();
  const getShowEpisode = useSelector(selectors.titles.getShowEpisode);
  const getMovie = useSelector(selectors.titles.getMovie);

  return (brandId: string, episodeId?: string, episode?: Maybe<Episode>) => {
    let subscriptions: Array<Maybe<SkySubscription>> | undefined;

    if (!auth?.isAuthenticated) {
      return undefined;
    }

    if (episode) {
      if (utils.entitlements.getVodSubscription(episode)) {
        return undefined;
      }
      subscriptions = utils.title.getSubscriptions(episode);
    } else if (episodeId) {
      const _episode = getShowEpisode(brandId, episodeId);
      if (utils.entitlements.getVodSubscription(_episode)) {
        return undefined;
      }
      subscriptions = utils.title.getSubscriptions(_episode);
    } else {
      const movie = getMovie(brandId);
      if (utils.entitlements.getVodSubscription(movie)) {
        return undefined;
      }
      subscriptions = utils.title.getSubscriptions(movie);
    }

    return subscriptions;
  };
};

export const useChannelSubscriptions = () => {
  const auth = useAuth();

  const getChannel = useSelector(selectors.channels.getChannel);
  return (channelId?: string, selectedChannel?: LinearChannel) => {
    if (!auth?.isAuthenticated) {
      return undefined;
    }
    const isSubscribed = channel => {
      return utils.entitlements.getChannelSubscription(channel)
        ? undefined
        : utils.title.getSubscriptions(channel);
    };
    if (selectedChannel) {
      return isSubscribed(selectedChannel);
    }
    if (channelId) {
      const channel = getChannel(channelId);
      return isSubscribed(channel);
    }
    return undefined;
  };
};

export const useSlotSubscriptions = () => {
  const auth = useAuth();

  return slot => {
    if (!auth?.isAuthenticated) {
      return undefined;
    }
    return utils.entitlements.getSlotSubscriptions(slot)
      ? undefined
      : utils.title.getSubscriptions(slot?.channel);
  };
};

const isLinearOnlyMovieOrShow = (brand: Movie | Show): boolean => {
  if (brand.__typename === BrandType.Movie && isNil(brand.asset)) {
    return true;
  }

  if (brand.__typename === BrandType.Show && (isNil(brand.seasons) || isEmpty(brand.seasons))) {
    return true;
  }

  return false;
};

export const useCheckVodOrChannelEntitled = () => {
  const requiresSubscriptions = useVodRequiredSubscriptions();
  return (brand: Movie | Show): boolean => {
    const firstAvailableSlot = utils.title.getFirstAvailableSlot(brand);
    const isSlotLive = utils.slot.isSlotOnAir(firstAvailableSlot);
    let isEntitled = false;

    if (isSlotLive || isLinearOnlyMovieOrShow(brand)) {
      // Entitlement subscription check for live tv
      isEntitled = utils.entitlements.getSlotSubscriptions(
        isNil(firstAvailableSlot) ? undefined : firstAvailableSlot,
      );
    } else if (brand.__typename === BrandType.Movie) {
      // Entitlement subscription check for vod movie
      isEntitled = !requiresSubscriptions(brand.id);
    } else if (brand.__typename === BrandType.Show) {
      // Entitlement subscription check for vod show
      isEntitled = !requiresSubscriptions(brand.id, undefined, brand.defaultEpisode);
    }
    return isEntitled;
  };
};

export const usePlaybackSubscriptions = () => {
  const getSubscriptions = useSelector(selectors.playback.getSubscriptions);
  return (mediaAssetId?: string) => {
    if (mediaAssetId) {
      return getSubscriptions(mediaAssetId);
    }
    return null;
  };
};

export const useUserSubscriptions = () => {
  const userSubscriptions = useSelector(selectors.subscription.subscriptions);
  return userSubscriptions;
};

export const useContentSubscriptionCheck = () => {
  const customerPackagesIds = useSelector(selectors.customer.customerPackagesIds);
  const { isAuthenticated } = useAuth();

  /**
   *
   * @param viewingMethod - The viewing method to check for subscription.
   * @returns {boolean} - Returns true if the viewing method has a valid subscription, false otherwise.
   *
   * Checks if the viewing method has a valid subscription with 3 conditions:
   * 1. If the viewing method has empty packages, it is considered free-to-air.
   * 2. If the customer has packages, it checks if any of the viewing method packages match the customer's packages.
   * 3. If the customer has no packages, it checks if any of the viewing method packages have a valid subscription.
   */
  const hasVodOrLinearSub = useCallback(
    (viewingMethod: ViewingMethods) => {
      // Check free-to-air
      if (isEmpty(viewingMethod?.packages)) {
        return true;
      }
      // Check from customerPackages
      if (customerPackagesIds && !isEmpty(customerPackagesIds)) {
        return viewingMethod?.packages?.some((packageItem: Maybe<CustomerPackage>) => {
          return packageItem?.id && customerPackagesIds?.includes(packageItem.id);
        });
      }
      return false;
    },
    [customerPackagesIds],
  );

  /**
   * Checks if the brand has a package upgrade available based on the viewing methods.
   *
   * @param brand - The brand (Movie, Show, or Episode) to check.
   * @param viewingMethodsToCheck - The type of viewing method to check (VOD or Linear).
   * @returns {boolean | undefined}
   * Returns true if the brand has a package upgrade available, false if it has a valid subscription,
   * or undefined if the brand does not have the specified viewing method.
   */
  const hasPkgUpgrade = useCallback(
    (brand: Movie | Show | Episode, viewingMethodsToCheck: VIEWING_METHODS) => {
      const viewingMethod = brand?.viewingMethods?.find(
        item => item?.__typename === viewingMethodsToCheck,
      ) as ViewingMethods;
      if (!viewingMethod || isEmpty(viewingMethod)) {
        return undefined;
      }
      return !hasVodOrLinearSub(viewingMethod);
    },
    [hasVodOrLinearSub],
  );

  /**
   * Checks if the brand has a package upgrade available for either VOD or Linear viewing methods.
   * @param brand - The brand (Movie or Show) to check.
   * @returns {boolean} - Returns true if the brand has a package upgrade available for either VOD or Linear, false otherwise.
   */
  const hasVodOrLinearPkgUpgrade = useCallback(
    (brand: Movie | Show): boolean => {
      const firstViewingMethod = brand?.viewingMethods[0];

      if (!firstViewingMethod) return false;
      if (firstViewingMethod?.__typename === VIEWING_METHODS.VOD) {
        return !!hasPkgUpgrade(brand, VIEWING_METHODS.VOD);
      }
      if (firstViewingMethod?.__typename === VIEWING_METHODS.LINEAR) {
        return !!hasPkgUpgrade(brand, VIEWING_METHODS.LINEAR);
      }
      return false;
    },
    [hasPkgUpgrade],
  );

  /**
   * Checks if the brand has a VOD (vodViewingMethods) package upgrade available.
   * @param brand - The brand (Movie or Show) to check.
   * @returns {boolean} - Returns true if the brand has a VOD (vodViewingMethod) package upgrade available, false otherwise.
   */
  const hasVodPkgUpgrade = useCallback(
    (brand: Movie | Show): boolean => {
      return !!hasPkgUpgrade(brand, VIEWING_METHODS.VOD);
    },
    [hasPkgUpgrade],
  );

  /**
   * Checks if the brand has watchable hero buttons based on authentication and package upgrades.
   * @param brand - The brand (Movie or Show) to check.
   * @returns {boolean} - Returns true if the brand has watchable hero buttons, false otherwise.
   */
  const hasWatchableHeroButtons = useCallback(
    (brand: Movie | Show): boolean => {
      if (!isAuthenticated) {
        return true; // If not authenticated, we display watchable buttons
      }
      const firstAvailableSlot = utils.title.getFirstAvailableSlot(brand);
      const isSlotLive = utils.slot.isSlotOnAir(firstAvailableSlot);

      if (brand?.__typename === BrandType.Movie) {
        return !hasPkgUpgrade(brand, VIEWING_METHODS.VOD);
      }
      if (brand?.__typename === BrandType.Show) {
        const defaultEpisode = brand?.defaultEpisode || ({} as Episode);
        // if asset is not available, we assume it's a linear show
        const viewingMethodsToCheck = defaultEpisode.asset?.id
          ? VIEWING_METHODS.VOD
          : VIEWING_METHODS.LINEAR;

        if (viewingMethodsToCheck === VIEWING_METHODS.LINEAR && isSlotLive) {
          // If the slot is live, we check for slot subscriptions instead of defaultEpisode
          return utils.entitlements.getSlotSubscriptions(firstAvailableSlot);
        }
        return hasPkgUpgrade(defaultEpisode, viewingMethodsToCheck) === false;
      }

      return false;
    },
    [hasPkgUpgrade, isAuthenticated],
  );
  return {
    hasVodOrLinearPkgUpgrade,
    hasPkgUpgrade,
    hasVodPkgUpgrade,
    hasWatchableHeroButtons,
  };
};
